import { usePage } from "@inertiajs/react"

import PlatformIcon from "@/components/icons/platform-icon"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { parseTextSegments } from "@/lib/text-segments"
import { cn } from "@/lib/utils"
import type { MentionHandleGroup, SharedProps } from "@/types"

interface Props {
  text: string
  className?: string
  mentionHandleGroups?: MentionHandleGroup[]
}

function MentionSpan({
  value,
  group,
  platformLabels,
}: {
  value: string
  group: MentionHandleGroup
  platformLabels: Record<string, string>
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground cursor-help">{value}</span>
      </TooltipTrigger>
      <TooltipContent sideOffset={6} className="max-w-64 space-y-2 px-3 py-2">
        {group.mention_handles.map((handle) => (
          <div
            key={`${group.label}-${handle.platform.name}`}
            className="flex items-center gap-2"
          >
            <PlatformIcon
              platform={handle.platform.name}
              className="size-3.5 shrink-0"
            />
            <span className="text-muted-foreground">
              {platformLabels[handle.platform.name] ?? handle.platform.name}
            </span>
            <span className="font-medium">{handle.handle}</span>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

function SegmentSpan({
  type,
  value,
  mentionGroupsByLabel,
  platformLabels,
}: {
  type: string
  value: string
  mentionGroupsByLabel: Record<string, MentionHandleGroup>
  platformLabels: Record<string, string>
}) {
  if (type === "mention") {
    const label = value.slice(1)
    const group = mentionGroupsByLabel[label]
    if (group) {
      return (
        <MentionSpan
          value={value}
          group={group}
          platformLabels={platformLabels}
        />
      )
    }
    return <span className="text-muted-foreground">{value}</span>
  }
  if (type === "hashtag") {
    return <span className="text-muted-foreground">{value}</span>
  }
  if (type === "url") {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:no-underline"
        onClick={(e) => e.stopPropagation()}
      >
        {value}
      </a>
    )
  }
  return <>{value}</>
}

export default function HighlightedText({
  text,
  className,
  mentionHandleGroups = [],
}: Props) {
  const { platforms } = usePage<SharedProps>().props
  const segments = parseTextSegments(text)
  const mentionGroupsByLabel = Object.fromEntries(
    mentionHandleGroups.map((group) => [group.label, group]),
  )
  const platformLabels = Object.fromEntries(
    platforms.map((platform) => [platform.name, platform.label]),
  )

  return (
    <p className={cn(className)}>
      {segments.map((segment, i) => (
        <SegmentSpan
          key={i}
          type={segment.type}
          value={segment.value}
          mentionGroupsByLabel={mentionGroupsByLabel}
          platformLabels={platformLabels}
        />
      ))}
    </p>
  )
}
